/**
 * Pure tracking functions extracted from TraversalStore.
 * Each function receives only the data it needs, keeping tracking
 * logic testable and independent of MobX store internals.
 */
import { toJS } from 'mobx'
import { dot } from 'dot-object'
import { trackInAmplitude, setUserProperties } from 'utils/tracking/amplitude'
import { trackTeal } from 'utils/tracking'
import { excludeFromCloack } from 'utils/tracking/amplitude/utils'
import { getCookie, COOKIE_KEY_FCID } from 'utils/cookie'
import { convertSlashToDotted } from 'utils/objectPath'
import { Data, FailuresItem, Patch } from 'interfaces/ApolloResponse.interface'
import {
  AmplitudeEvent,
  ErrorType,
  PathToTracking,
  successTrackableFields
} from 'utils/tracking/amplitude/amplitude.interface'

export interface TraversalTealContext {
  affiliateId: string | number | undefined
  subId: string | undefined
  refId: string | undefined
  configurationId: string | undefined
  advertisementId: string | undefined
  traversalPersonId: string | undefined
}

/**
 * Tracks amplitude events for each field that failed validation.
 */
// eslint-disable-next-line import/prefer-default-export
export const trackValidationErrors = (
  failures: FailuresItem[],
  patches: Patch[] = []
): void => {
  const patchFields = patches.map((item) => item.path)

  failures.forEach(({ fields, id: errors }) => {
    fields.forEach((field) => {
      if (patchFields.includes(field)) {
        trackInAmplitude(AmplitudeEvent.ValidateTraversal, {
          type: ErrorType.Field,
          path: convertSlashToDotted(field),
          errors
        })
      }
    })
  })
}

/**
 * Tracks amplitude events for successfully validated fields.
 * Also fires a Teal event when income is set for the primary debtor.
 */
export const trackValidationSuccess = (
  failures: FailuresItem[],
  patches: Patch[] = [],
  tealContext: TraversalTealContext
): void => {
  const successTrackedFields = patches
    .filter((item) => successTrackableFields.indexOf(item.path) > -1)
    .map((item) => item.path)

  const erroredPaths: string[] = []
  if (successTrackedFields.length) {
    failures.forEach(({ fields }) => {
      fields.forEach((field) => erroredPaths.push(field))
    })
  }

  successTrackedFields.forEach((path) => {
    if (erroredPaths.indexOf(path) === -1) {
      // @ts-ignore - PathToTracking type needs refinement for dynamic access
      trackInAmplitude(PathToTracking[path])

      if (path === '/debtors/primary/income/primary') {
        const {
          affiliateId,
          subId,
          refId,
          configurationId,
          traversalPersonId,
          advertisementId
        } = tealContext

        trackTeal({
          transaction: { event: 'income_set_successfully' },
          affiliateId,
          subId,
          refId,
          configurationId,
          traversalPersonId,
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            userAttributes: { advertisementId }
          }
        })
      }
    }
  })
}

export interface TraversalEventData {
  purpose: string | undefined
  amount: number | undefined
  term: number | undefined
  subId: string | undefined
  refId: string | undefined
  configurationId: string | undefined
  affiliateId: string | number | undefined
  traversalPersonId: string | undefined
  currentTraversalId: string | null
}

/**
 * Fires the traversal Teal tracking event.
 */
export const trackTraversalEvent = (
  eventData: TraversalEventData,
  traversalId?: string
): void => {
  const {
    purpose,
    amount,
    term,
    subId,
    refId,
    configurationId,
    affiliateId,
    traversalPersonId,
    currentTraversalId
  } = eventData

  trackTeal({
    transaction: {
      event: 'traversal',
      label: '/kreditantrag/debtorCount',
      traversalId: currentTraversalId || traversalId,
      amount,
      purpose,
      term,
      category: 'loan_status'
    },
    affiliateId,
    subId,
    refId,
    configurationId,
    traversalPersonId
  })
}

/**
 * Collects user properties from traversal data for Amplitude.
 * Returns the property object so the caller can pass it to setUserProperties.
 */
export const collectUserPropertiesForPut = (
  data: Data,
  arrayOfPathsOnThePage: string[]
): Record<string, unknown> => {
  const userPropertyObject: Record<string, unknown> = {}
  const traversalDataObject = dot(toJS(data))

  const updatedPathsInPut = excludeFromCloack.filter((value) =>
    arrayOfPathsOnThePage.includes(value)
  )

  updatedPathsInPut.forEach((property) => {
    if (
      property.indexOf('debtors.secondary') > -1 &&
      // @ts-ignore - debtors.count dynamic access
      traversalDataObject['debtors.count'] < 2
    ) {
      return
    }

    if (traversalDataObject[property]) {
      userPropertyObject[property] = traversalDataObject[property]
    }
  })

  return userPropertyObject
}

/**
 * Builds and sends user properties to Amplitude.
 * Async because getAllPathsOnPage() is async.
 */
export const sendUserPropertiesOnPut = async (
  data: Data,
  getAllPathsOnPage: () => Promise<string[]>
): Promise<void> => {
  const arrayOfPathsOnThePage = await getAllPathsOnPage()
  const userPropertyObject = collectUserPropertiesForPut(
    data,
    arrayOfPathsOnThePage
  )
  setUserProperties(userPropertyObject)
}
